import { z } from 'zod';
import { CheckoutComplete, Product } from '../state/state.types';
import {
  BulletItem,
  StepContent,
  StepRecordContentByAmountByDependency,
  StepRecordContentByDependency,
} from './stepContent.types';

export const checkoutContent: StepRecordContentByDependency<CheckoutComplete> = {
  false: {
    header: 'My To-Do List',
    description: 'Once the below is completed, your Advocate will reach out to start the process.',
  },
  true: {
    header: 'Welcome to Resolve!',
    description:
      "Thanks again for putting your trust in Resolve. We're excited to get started lowering your medical bills. Our team is currently reviewing and you can expect an email from your Advocate shortly.",
  },
};

const protectBulletList: BulletItem[] = [
  {
    id: '1',
    text: 'No savings found? Your deposit is refunded automatically.',
    iconName: 'ArrowRight',
    boldWordNumber: 3,
  },
];

const pointInTime_10 = {
  9900: {
    title: 'Start the Process',
    bulletList: [
      {
        id: '1',
        text: 'Make a $499 no risk deposit to get started.',
      },
      {
        id: '2',
        text: "If we don't find you savings, we'll issue you a refund.",
      },
      {
        id: '3',
        text: 'If we do find you savings, your deposit is applied to your 10% success fee.',
      },
    ],
  },
  25000: {
    title: 'Start the Process',
    description:
      "Enter your card information. It is fully refundable if we don't find you any savings.",
  },
};

const pointInTime_08 = {
  9900: {
    title: 'Start the Process',
    bulletList: [
      {
        id: '1',
        text: 'Make a $250 no risk deposit to get started.',
      },
      {
        id: '2',
        text: "If we don't find you savings, we'll issue you a refund.",
      },
      {
        id: '3',
        text: 'If we do find you savings, your deposit is applied to your 25% success fee.',
      },
    ],
  },
  25000: {
    title: 'Start the Process',
    description:
      "Enter your card information. It is fully refundable if we don't find you any savings.",
  },
};

const pointInTime_20 = {
  9900: {
    title: 'Start the Process',
    bulletList: [
      {
        id: '1',
        text: 'Make a $250 no risk deposit to get started.',
      },
      {
        id: '2',
        text: "If we don't find you savings, we'll issue you a refund.",
      },
      {
        id: '3',
        text: 'If we do find you savings, your deposit is applied to your 25% success fee.',
      },
    ],
  },
  25000: {
    title: 'Start the Process',
    description:
      "Enter your card information. It is fully refundable if we don't find you any savings.",
  },
};

const pointInTime_25 = {
  9900: {
    title: 'Start the Process',
    bulletList: [
      {
        id: '1',
        text: 'Make a $250 no risk deposit to get started.',
      },
      {
        id: '2',
        text: "If we don't find you savings, we'll issue you a refund.",
      },
      {
        id: '3',
        text: 'If we do find you savings, your deposit is applied to your 25% success fee.',
      },
    ],
  },
  25000: {
    title: 'Start the Process',
    description:
      "Enter your card information. It is fully refundable if we don't find you any savings.",
  },
};

export const paymentCardContent: StepRecordContentByAmountByDependency<Product> = {
  'bill-reduction-25': pointInTime_25,
  'bill-reduction-10': pointInTime_10,
  'bill-reduction-08': pointInTime_08,
  'bill-reduction-20': pointInTime_20,
  subscription: {
    4999: {
      title: 'Your $49.99 monthly fee',
      description:
        "Enter your card information. You can cancel at any time. We'll work on bills as long as your monthly subscription is active.",
    },
  },
  ezfile: {
    4900: {
      title: '$49 per provider',
      bulletList: protectBulletList,
    },
  },
};

export const partnershipContent = (
  partnership: string,
  activatedServices: boolean,
): StepContent => ({
  title: 'Activate Resolve Services',
  description: activatedServices
    ? 'Thanks for getting started with Resolve. Your advocate will reach out to you within 1-2 business days to follow up with next steps'
    : `Thanks to our partnership with ${partnership}, you aren't required to make a deposit today. Simply activate Resolves services and our team will get to work reducing your bills.`,
});

const schedulingCallStepContent: StepContent = {
  title: 'Schedule a Call',
};

const hipaaDefaultStepContent: StepContent = {
  title: 'Let Us Represent You',
  description:
    'Sign a HIPAA authorization that will let us speak to hospitals and insurance companies on your behalf.',
};

export const hipaaCardContent: StepRecordContentByDependency<Product> = {
  'bill-reduction-25': hipaaDefaultStepContent,
  'bill-reduction-10': hipaaDefaultStepContent,
  'bill-reduction-08': hipaaDefaultStepContent,
  'bill-reduction-20': hipaaDefaultStepContent,
  subscription: hipaaDefaultStepContent,
  ezfile: {},
};

export const schedulingCallCardContent: StepRecordContentByDependency<Product> = {
  'bill-reduction-08': schedulingCallStepContent,
  'bill-reduction-10': schedulingCallStepContent,
  'bill-reduction-20': schedulingCallStepContent,
  'bill-reduction-25': schedulingCallStepContent,
  subscription: schedulingCallStepContent,
  ezfile: schedulingCallStepContent,
};

export const termsAndConditionsContent: Record<
  'bill-reduction-25' | 'bill-reduction-10' | 'bill-reduction-08' | 'bill-reduction-20',
  string
> = {
  'bill-reduction-10':
    'I will pay Resolve 10% of any savings found. If savings found are less than $5,000 I will pay the lesser of 50% of savings found or $2,500. In no event will I have to pay more than 50% of what I saved.',
  'bill-reduction-25':
    'I will pay Resolve 25% of any savings found, with a minimum fee of $1,000 for services rendered. If savings found are less than $2,000 I will only pay 50% of savings found.',
  'bill-reduction-08':
    'I will pay Resolve <del style="color:#A1A1A1">10%</del> <b>8%</b> of any savings found. If savings found are less than $4,000 I will pay the lesser of 50% of savings found or <del style="color:#A1A1A1">$2,500</del> <b>$2,000</b>. In no event will I have to pay more than 50% of what I saved.',
  'bill-reduction-20':
    'I will pay Resolve <del style="color:#A1A1A1">25%</del> 20% of any savings found. If savings found are less than $1,600 I will pay the lesser of 50% of savings found or <del style="color:#A1A1A1">$1,000</del> <b>$800</b>. In no event will I have to pay more than 50% of what I saved.',
};

export const Environment = z.enum(['production', 'development']);

type Environment = z.infer<typeof Environment>;
export const tcLinkContent: Record<Environment, Record<Product, Record<number, string>>> = {
  development: {
    'bill-reduction-25': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_25.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_25.pdf',
    },
    'bill-reduction-10': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_10.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_10.pdf',
    },
    subscription: {
      4999: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_25.pdf',
    },
    ezfile: '',
    'bill-reduction-08': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_08_29_09_25.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_08_29_09_25.pdf',
    },
    'bill-reduction-20': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_20_29_09_25.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_20_29_09_25.pdf',
    },
  },
  production: {
    'bill-reduction-25': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_25.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_25.pdf',
    },
    'bill-reduction-10': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_10.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_10.pdf',
    },
    subscription: {
      4999: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_25_09_25.pdf',
    },
    ezfile: '',
    'bill-reduction-08': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_08_29_09_25.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_08_29_09_25.pdf',
    },
    'bill-reduction-20': {
      9900: 'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_20_29_09_25.pdf',
      25000:
        'https://resolvemedicalbills-questionnaire-production.s3.us-east-1.amazonaws.com/static-assets/tc_20_29_09_25.pdf',
    },
  },
};

export const noDepositPartnershipTC =
  'https://resolvemedicalbills-questionnaire-production.s3.amazonaws.com/static-assets/Resolve+Contract+and+Work+Statement+%5BPartnerships%5D.pdf';
